import type { HumanoidPose } from './AttachmentGeometry';

export const CROSSBOW_PRIMARY_GRIP = { x: 5, y: 10 } as const;

/** Includes the attack support grip for all current body shapes. */
export const CROSSBOW_LOCAL_BOUNDS = { minX: -24, maxX: 24, minY: -15, maxY: 18 } as const;

/**
 * Conservative art envelope including curve control points. A rectangular
 * bound's empty upper-right corner overstates clipping during rotated poses.
 */
export const CROSSBOW_LOCAL_ENVELOPE = [
  { x: -24, y: 3 }, { x: -24, y: 13 }, { x: -21, y: 15 },
  { x: -12, y: 17 }, { x: 2, y: 18 },
  { x: 8, y: 18 }, { x: 11, y: 15 }, { x: 21, y: 11 },
  { x: 24, y: 0 }, { x: 21, y: -11 }, { x: 11, y: -15 },
  { x: 9, y: -15 },
] as const;

export interface CrossbowArtworkOptions {
  pose?: HumanoidPose;
  /** Back-hand anchor after the owner's inverse weapon transform. */
  secondaryGrip?: Readonly<{ x: number; y: number }>;
}

function attackSupport(grip: Readonly<{ x: number; y: number }>, accentColor: string): string {
  const { x, y } = grip;
  return `<g data-crossbow-support="back-hand">
    <!-- Open trigger guard leaves the existing rear hand visible at its centre. -->
    <path d="M${x - 4},${y - 4} L${x - 4},${y + 2} Q${x},${y + 6} ${x + 4},${y + 2} L${x + 4},${y - 4}" fill="none" stroke="${accentColor}" stroke-width="1.2"/>
    <path d="M${x - 3},6 L${x - 3},${y - 4} L${x + 3},${y - 4} L${x + 3},6 Z" fill="#493324"/>
    <circle data-weapon-secondary-grip="back-hand" cx="${x}" cy="${y}" r="0"/>
  </g>`;
}

/**
 * Loaded crossbow pointing along local +X; the bolt lies on y=0. WeaponParts
 * owns rotation (attack must be 0 degrees), metadata and the (5,10) hand grip.
 * Only attack uses the rear hand: other poses carry the same compact weapon
 * one-handed rather than stretching a stock across the relaxed body rig.
 * No SVG ids, filters, animation, body transforms or equipment metadata here.
 */
export function renderCrossbowArtwork(
  primaryColor: string,
  accentColor: string = primaryColor,
  options: CrossbowArtworkOptions = {},
): string {
  const requestedGrip = options.secondaryGrip;
  const secondaryGrip = requestedGrip && Number.isFinite(requestedGrip.x) && Number.isFinite(requestedGrip.y)
    ? requestedGrip
    : { x: -14, y: 10 };
  const support = options.pose === 'attack' ? attackSupport(secondaryGrip, accentColor) : '';

  return `<g data-weapon-art="crossbow">
    <!-- Bent limbs and a drawn string identify a crossbow before its decoration. -->
    <path d="M11,-15 Q21,-11 21,0 Q21,11 11,15 L10,12 Q17,8 17,0 Q17,-8 10,-12 Z" fill="${primaryColor}"/>
    <path d="M11,-13 L-8,0 L11,13" fill="none" stroke="#ded7c7" stroke-width="1.1"/>
    <path d="M10,-14 L13,-13 L12,-10 L10,-11 Z M10,14 L13,13 L12,10 L10,11 Z" fill="${accentColor}"/>
    <!-- Shaped wooden shoulder stock, rail and metal binding. -->
    <path d="M-24,3 L16,3 L19,5 L13,8 L-14,8 L-20,13 L-24,13 Z" fill="${primaryColor}"/>
    <path d="M-23,9 L-15,5 L12,5 L12,7 L-15,7 L-21,12 L-23,12 Z" fill="#171a20" opacity="0.3"/>
    <path d="M-21,3 L16,3 L16,4.5 L-21,4.5 Z" fill="#ffffff" opacity="0.23"/>
    <path d="M-24,3 L-21,3 L-21,13 L-24,13 Z" fill="${accentColor}"/>
    <path d="M12,2 L17,2 L17,8 L12,8 Z" fill="${accentColor}"/>
    <path d="M13,2 L14,2 L14,8 L13,8 Z" fill="#ffffff" opacity="0.28"/>
    ${support}
    <!-- Foregrip is centred on the existing lead hand and covered by its glove. -->
    <path d="M2,5 L8,5 L8,18 L2,18 Z" fill="#493324"/>
    <path d="M3,6 L5,6 L5,17 L3,17 Z" fill="${primaryColor}"/>
    <path d="M2,7 L8,7 L8,8 L2,8 Z M2,13 L8,13 L8,14 L2,14 Z" fill="${accentColor}"/>
    <!-- Loaded bolt: shaft, fletching and arrowhead share one horizontal axis. -->
    <g data-aim-axis="right">
      <path d="M-10,-0.8 L20,-0.8 L20,0.8 L-10,0.8 Z" fill="#d7d2c4"/>
      <path d="M24,0 L18,-3 L19,0 L18,3 Z" fill="${accentColor}"/>
      <path d="M-8,0 L-12,-3 L-8,-2 L-5,0 L-8,2 L-12,3 Z" fill="${accentColor}"/>
    </g>
    <circle data-crossbow-grip="front-hand" cx="5" cy="10" r="0"/>
  </g>`;
}
